<script lang="ts">
	/**
	 * AddEditButtons Component
	 *
	 * This component provides a unified interface for add/edit operations and batch commands
	 * in different contexts (rooms, devices, general). It manages the display of various buttons
	 * based on edit state, user permissions, and device context.
	 *
	 * Key Features:
	 * - Add button for creating new items
	 * - Edit button for entering edit mode
	 * - Exit edit button for leaving edit mode
	 * - Info button for mobile drawer access
	 * - Integrated BatchCommandButtons for bulk operations
	 *
	 * Context-aware rendering:
	 * - 'rooms': Shows batch commands with specific user and edit state conditions
	 * - 'room': Shows batch commands with specific user and edit state conditions
	 * - 'devices': Shows batch commands always (regardless of user type)
	 * - 'general': No batch commands shown
	 */

	/**
	 * COMPONENT RENDERING LOGIC
	 *
	 * The component renders different button combinations based on edit state:
	 *
	 * NON-EDIT MODE (!$edit || disableEditMode):
	 * 1. Add button (if not hidden)
	 * 2. Edit button (if not hidden)
	 * 3. BatchCommandButtons (if conditions met, positioned after edit button)
	 * 4. Info button (mobile only, if not in edit mode, positioned last)
	 *
	 * EDIT MODE ($edit && $initialEdit):
	 * 1. Exit edit button
	 * 2. BatchCommandButtons (if conditions met, positioned after exit button)
	 * 3. Info button (mobile only, if not in edit mode, positioned last)
	 */
	import { createEventDispatcher, onDestroy } from 'svelte';
	import { format, init } from 'svelte-i18n';
	//Stores
	import { edit, initialEdit, update } from 'src/stores/ui/edit_lists';
	//Design system components
	import { Button, IconButton } from '@Shelly/shelly-design-system';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import { resetDrawer } from 'src/helpers/drawer_helpers';
	//BatchCommandButtons dependencies
	import { isDevServerUser, isT1User } from 'src/helpers/user_server_conditions';
	import BatchCommandButtons from './BatchCommandButtons.svelte';
	import { setSidePanelState } from './Sidepanel/sidepanel-helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	/**
	 * Event dispatcher for add/edit button clicks.
	 * Emits 'add_click' when the add button is pressed and 'edit_click' when the
	 * edit button is pressed while `interceptEditClick` is enabled.
	 */
	const dispatch = createEventDispatcher<{ add_click: {}; edit_click: {} }>();

	// Button visibility and state props
	export let hideAddButton = false; // Hide the add (plus) button
	export let hideEditButton = false; // Hide the edit (pen) button
	export let disableEditMode = false; // Disable edit mode functionality
	export let disableEditButton = false; // Disable the edit button
	export let disableAddButton = false; // Disable the add button
	export let interceptEditClick = false; // Dispatch 'edit_click' instead of entering edit mode (e.g. shared homes)

	/**
	 * Handles edit button presses. On shared homes (interceptEditClick) the
	 * parent decides what happens (e.g. show the "managed by owner" modal)
	 * instead of entering edit mode.
	 */
	const onEditClick = () => {
		if (interceptEditClick) {
			dispatch('edit_click');
			return;
		}
		resetDrawer();
		edit.set(true);
		initialEdit.set(true);
	};

	// BatchCommandButtons integration props
	export let areFiltersDisabled: boolean = false; // Control filter state in batch operations
	export let disableBatchCommandButton = false; // Disable batch command dropdown

	// Context props for conditional rendering
	export let context: 'room' | 'devices' | 'general' | 'rooms' = 'general'; // Determines which batch command rules to apply
	export let isMobile = false; // Mobile state for responsive behavior
	export let useSecondaryEllipsisVariant: boolean = false; // Control icon button variant in BatchCommandButtons

	const DESKTOP_SCREEN_MIN = SystemScreenBreakpoints.DESKTOP_MIN;
	let windowClientWidth: number; // Current window width for responsive behavior
	let isTestUser = isDevServerUser(['repo', 'nb']) || isT1User(); // Check if user has test permissions

	/**
	 * Centralized logic for BatchCommandButtons visibility
	 * Determines when to show batch command buttons based on:
	 * - User permissions (test/non-test users with different rules per context)
	 * - Context type (room, devices, general)
	 * - Edit state and update mode
	 * - Mobile/desktop view
	 *
	 * Context-specific rules:
	 * - 'room': Complex logic with user-specific conditions:
	 *          • For non-test users: Hide only when in initial edit mode
	 *          • For all users: Show in initial edit, edit+update mode, non-update mode, or desktop+update mode
	 * - 'devices': Always show batch commands regardless of user type or state
	 * - 'general': Never show
	 */
	$: shouldShowBatchCommandButtons = (() => {
		switch (context) {
			case 'room':
				// For all users in rooms: show based on edit state and mobile/desktop conditions
				return (
					$initialEdit ||
					($edit && !$initialEdit && $update.value) ||
					!$update.value ||
					(!isMobile && $update.value)
				);

			case 'devices':
				// In devices list: always show batch commands regardless of user type or state
				return true;

			default:
				return false;
		}
	})();

	onDestroy(() => {
		// Reset the initial edit state when the component is destroyed
		// This ensures clean state management when navigating between pages
		initialEdit.set(false);
	});
</script>

<!-- // TODO If info icon stays, add utility classes and fix grid gap in toolbar when slot is hidden-->
<svelte:window bind:innerWidth={windowClientWidth} />

<!-- Add button - only in non-edit mode -->
<!-- Shows plus icon button for creating new items -->
{#if (!$edit || disableEditMode) && !hideAddButton}
	<IconButton
		aria-label={$format('button_add', { default: 'Add button' })}
		class="align-self-center"
		icon="fas fa-plus"
		on:click={() => {
			dispatch('add_click');
		}}
		disabled={disableAddButton}
	/>
{/if}

<!-- Edit button - only in non-edit mode -->
<!-- Shows pen icon button to enter edit mode -->
{#if (!$edit || disableEditMode) && !hideEditButton && context !== 'rooms'}
	<IconButton
		aria-label={$format('button_edit', { default: 'Edit button' })}
		class="align-self-center"
		icon="fas fa-pen"
		disabled={disableEditButton}
		on:click={onEditClick}
	/>
{:else if (!$edit || disableEditMode) && !hideEditButton && context === 'rooms' && $showNewLayout}
	<Button
		size="m"
		variant="secondary"
		class="align-self-center"
		icon="fa-solid fa-arrows-up-down-left-right"
		disabled={disableEditButton}
		on:click={onEditClick}
	>
		<!-- TODO: Add new translation -->
		{$format('label_reorder_items', { default: 'Reorder items' })}
	</Button>
{/if}

<!-- Exit edit button - only when in initialEdit mode -->
<!-- Shows text button to exit edit mode and return to normal view -->
{#if $edit && $initialEdit && context !== 'rooms' && !$showNewLayout}
	<Button
		class="align-self-center text-capitalize"
		size="m"
		on:click={() => {
			edit.set(false);
			initialEdit.set(false);
		}}
	>
		{$format('button_exit_edit', { default: 'exit edit mode' })}
	</Button>
{:else if $edit && $initialEdit && context === 'rooms' && $showNewLayout}
	<Button
		size="m"
		variant="secondary"
		class="align-self-center"
		on:click={() => {
			edit.set(false);
			initialEdit.set(false);
		}}
	>
		{$format('label_finish_reordering', { default: 'Finish reordering' })}
	</Button>
{/if}

<!-- BatchCommandButtons positioned strategically for optimal UX -->
<!-- Shows after edit/exit-edit buttons but before info button for proper visual hierarchy -->
<!-- Provides bulk operations like update, delete, move devices, etc. -->
<!-- Visibility controlled by shouldShowBatchCommandButtons computed property with context-specific rules -->
{#if shouldShowBatchCommandButtons}
	<BatchCommandButtons
		bind:isMobile
		on:select-all
		on:submit-click
		bind:areFiltersDisabled
		{disableBatchCommandButton}
		{useSecondaryEllipsisVariant}
	/>
{/if}

<!-- INFO BUTTON - shows only on mobile and when not in edit mode -->
<!-- Provides access to drawer/sidebar information panel -->
{#if windowClientWidth < DESKTOP_SCREEN_MIN && !$edit && !$showNewLayout && context !== 'rooms'}
	<IconButton
		aria-label={$format('button_info', { default: 'Info button' })}
		class="align-self-center"
		variant="secondary"
		icon="fas fa-circle-info"
		on:click={() => setSidePanelState({ open: true })}
	/>
{/if}
